import { api } from '../../core/network/api';
import type { ArtifactRarity, ArtifactType } from './inventory-api';

// SOFT = silver (consumables) · PREMIUM = runes (boosts + vault).
export type ShopCurrency = 'SOFT' | 'PREMIUM';
export type ShopGroup = 'consumable' | 'boost' | 'vault';
export type ShopEffectKind =
  | 'nerve_refill'
  | 'hp_refill'
  | 'hp_partial'
  | 'hp_fixed'
  | 'xp_booster'
  | 'cash_rush'
  | 'revive'
  | 'vault_artifact'
  // Golden Chest gacha pull + Sea-Chest Expansion — same catalog shape, rendered
  // with bespoke ornate cards in Market.vue's Supplies & Favors tab. Optional
  // client-side widening: harmless no-op if the backend hasn't shipped them yet.
  | 'chest_pull'
  | 'inventory_slot';

/** A catalog entry as shown in the storefront. */
export interface ShopItem {
  id: string;
  name: string;
  description: string;
  price: number;
  currency: ShopCurrency;
  group: ShopGroup;
  kind: ShopEffectKind;
  durationMs: number | null;
  dailyLimit: number | null;
  /** Effect strength for magnitude-based consumables (hp_partial 0.5 = +50% health). */
  magnitude?: number;
  /** hp_fixed: flat HP units restored (fixed price — no roof scaling). */
  amount?: number | null;
  /** vault_artifact only: the stat spec of the gear on offer — NESTED, exactly
   *  as the backend ships it (`shop.service.ts` maps `vault: i.vault ?? null`). */
  vault: VaultArtifactSpec | null;
}

/** A purchasable Vault artifact (special gear bought with runes). */
export interface VaultArtifactSpec {
  name: string;
  description: string;
  rarity: ArtifactRarity;
  type: ArtifactType;
  attack: number;
  defense: number;
  magic: number;
  speed: number;
  hp: number;
  level: number;
  setName?: string | null;
  statusEffect?: string | null;
}

/** The caller's relevant state (drives "already full" / cap / buff hints). */
export interface ShopState {
  hp: number;
  maxHp: number;
  nerve: number;
  maxNerve: number;
  cashRushUntil: string | null;
  xpBoosterUntil: string | null;
  hasRevive: boolean;
  nerveRefillsUsedToday: number;
  /** Sea-Chest capacity — mirrors GET /gear so the Shop can show it without a second fetch. */
  bagCount: number;
  bagCap: number;
}

/** A preset gold-bars → cash package offered in the Counting House exchange modal. */
export interface ExchangePackage {
  id: string;
  name: string;
  bars: number;
  gross: number;
  commission: number;
  credited: number;
}

/** Sea-Chest-slot expansion terms (tiered + capped) — drives the hero-page button
 *  and The Quarter's expansion card. `nextPrice` is null once at `max`. */
export interface SlotTerms {
  current: number;
  base: number;
  max: number;
  step: number;
  nextPrice: number | null;
}

/** Custom-amount laundering terms (any bar count at the base rate). */
export interface ExchangeCustomTerms {
  minBars: number;
  maxBars: number;
  cashPerBar: number;
}

export interface ShopView {
  /** Account/XP level — feeds the item-expansion-14-08 §3.4 minLevel gate preview. */
  level: number;
  catalog: ShopItem[];
  balance: { soft: number; premium: number };
  state: ShopState | null;
  exchange: { commissionPct: number; packages: ExchangePackage[]; custom: ExchangeCustomTerms };
  slots: SlotTerms;
}

/** A Vault artifact granted by a purchase (now in the bag). */
export interface GrantedArtifact {
  id: string;
  name: string;
  rarity: ArtifactRarity;
  type: ArtifactType;
}

export interface ShopBuyResult {
  bought: string;
  name: string;
  price: number;
  currency: ShopCurrency;
  granted: GrantedArtifact | null;
  balance: { soft: number; premium: number };
  state: ShopState | null;
}

/** GET /api/shop — catalog + balances + relevant buff/cap state. */
export const fetchShop = () => api.get<ShopView>('/shop');

/** POST /api/shop/buy — debit currency, apply effect / grant vault gear. */
export const buyShopItem = (itemId: string) =>
  api.post<ShopBuyResult>('/shop/buy', { itemId });

export interface ExchangeResult {
  exchanged: true;
  packageId: string;
  bars: number;
  gross: number;
  commission: number;
  credited: number;
  balance: { soft: number; premium: number };
}

/** POST /api/shop/exchange — The Counting House: a fixed package OR a custom bar amount. */
export const exchangeCash = (body: { packageId?: string; bars?: number }) =>
  api.post<ExchangeResult>('/shop/exchange', body);
